import { useLanguage } from "@/context/language"
import { listActivityLogs, type CoworkActivityLog } from "@/cowork/api"
import { DialogBody, DialogHeader, DialogTitle, DialogV2 } from "@opencode-ai/ui/v2/dialog-v2"
import { createMemo, createResource, For, Show } from "solid-js"
import { createStore } from "solid-js/store"

export function DialogCoworkActivityLogs() {
  const language = useLanguage()
  const [store, setStore] = createStore({ search: "", kind: "" })
  const [logs, actions] = createResource(() => ({ kind: store.kind, search: store.search, limit: 150 }), listActivityLogs)
  const count = createMemo(() => logs()?.length ?? 0)

  return (
    <DialogV2 size="x-large" variant="settings" class="!overflow-hidden">
      <DialogHeader closeLabel={language.t("common.close")}>
        <DialogTitle>{language.t("cowork.activity.title")}</DialogTitle>
      </DialogHeader>
      <DialogBody class="min-h-0 flex-1 gap-0 overflow-hidden p-0">
        <div class="sticky top-0 z-10 border-b border-v2-border-border-base bg-v2-background-bg-layer-01/95 p-4 backdrop-blur">
          <div class="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div class="text-13-medium uppercase tracking-[0.16em] text-v2-text-text-muted">{language.t("cowork.activity.title")}</div>
            <div class="text-12-regular text-v2-text-text-muted">{language.t("cowork.activity.count", { count: String(count()) })}</div>
          </div>
          <div class="flex flex-wrap items-center gap-3">
          <input
            class="h-10 min-w-[260px] flex-1 rounded-lg border border-v2-border-border-base bg-v2-background-bg-base px-3 text-13-regular text-v2-text-text-base outline-none placeholder:text-v2-text-text-faint focus:border-v2-icon-icon-accent"
            value={store.search}
            placeholder={language.t("cowork.activity.search")}
            onInput={(event) => setStore("search", event.currentTarget.value)}
          />
          <select
            class="h-10 rounded-lg border border-v2-border-border-base bg-v2-background-bg-base px-3 text-13-regular text-v2-text-text-base outline-none focus:border-v2-icon-icon-accent"
            value={store.kind}
            onChange={(event) => setStore("kind", event.currentTarget.value)}
          >
            <option value="">{language.t("cowork.activity.filter.all")}</option>
            <option value="audit">{language.t("cowork.activity.filter.audit")}</option>
            <option value="message">{language.t("cowork.activity.filter.message")}</option>
          </select>
          <button class="h-10 rounded-lg border border-v2-border-border-base bg-v2-background-bg-base px-3 text-13-medium text-v2-text-text-base hover:bg-v2-background-bg-layer-02" onClick={() => actions.refetch()}>
            {language.t("cowork.activity.refresh")}
          </button>
          </div>
        </div>
        <div class="min-h-0 flex-1 space-y-3 overflow-y-auto p-4 [scrollbar-color:var(--v2-border-border-base)_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin]">
          <For each={logs() ?? []} fallback={<div class="rounded-2xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-8 text-center text-13-regular text-v2-text-text-muted">{language.t("cowork.activity.empty")}</div>}>
            {(item) => <ActivityLogRow item={item} />}
          </For>
        </div>
      </DialogBody>
    </DialogV2>
  )
}

function ActivityLogRow(props: { item: CoworkActivityLog }) {
  return (
    <article class="rounded-2xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-4 shadow-[var(--v2-elevation-card)]">
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div class="min-w-0">
          <div class="flex flex-wrap items-center gap-2">
            <span class="rounded-full border border-v2-border-border-base bg-v2-background-bg-base px-2 py-0.5 text-11-medium uppercase tracking-[0.08em] text-v2-text-text-muted">{props.item.kind}</span>
            <h3 class="truncate text-14-medium text-v2-text-text-base">{props.item.title}</h3>
          </div>
          <div class="mt-1 text-12-regular text-v2-text-text-muted">{props.item.event_type}</div>
        </div>
        <time class="shrink-0 text-12-regular text-v2-text-text-muted">{new Date(props.item.created_at).toLocaleString()}</time>
      </div>
      <div class="mt-3 grid gap-3 lg:grid-cols-[minmax(0,1.25fr)_minmax(260px,0.75fr)]">
        <p class="max-h-36 overflow-auto whitespace-pre-wrap rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3 text-12-regular leading-5 text-v2-text-text-base [scrollbar-color:var(--v2-border-border-base)_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin]">
          {props.item.description || "-"}
        </p>
        <div class="rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-3 text-11-regular text-v2-text-text-muted">
          <Show when={Object.entries(props.item.details ?? {}).length > 0} fallback={<div>-</div>}>
            <For each={Object.entries(props.item.details ?? {})}>
            {([label, value]) => (
              <div class="grid grid-cols-[120px_minmax(0,1fr)] gap-2 border-b border-v2-border-border-base/60 py-1.5 last:border-b-0">
                <span class="truncate text-v2-text-text-faint">{label}</span>
                <span class="min-w-0 break-words text-v2-text-text-muted">{value || "-"}</span>
              </div>
            )}
            </For>
          </Show>
        </div>
      </div>
    </article>
  )
}
